import type {
  PointsInvoice,
  PointsOrder,
  PointsOrderPage,
  PointsPackage,
  PointsRefund,
  PointsRefundQuote,
  PointsStatus,
} from '@/types/points';

/**
 * PointsService — Mentor Creative 點數(購點 / 回跳入點 / 退款 / 發票)
 * -----------------------------------------------------------------------------
 * 契約 SoT = Creative BE **docs/141 v2.1**(`/api/wallet/mentor/*`;BE 主導,2026-11-02 定案)。
 * FE 只打 Creative BE,由它轉 Mentor partner API。
 *
 * 三條流程:
 *   - **購點**:`createOrder` → 整頁導去回應的 `payUrl`(Mentor 付款頁,要登入 Mentor 一次)→
 *     Mentor 付款完 302 回 `returnUrl`(**FE 自己把 `order_ref` 放進 query**,BE 不改寫;Mentor 會
 *     保留原 query 再疊 `status` / `display_id`)→ 回跳頁以 `getOrder(ref, {refresh:true})` 輪詢到入點。
 *   - **繼續付款**:待付款單 `getPayUrl` 重取付款頁連結(限時,每次按都重取)。
 *   - **退款**:`getRefundQuote` 試算(BE 算公式 + Mentor 手續費)→ `requestRefund`(`refundId` = FE
 *     自鑄冪等鍵;`quotedAmount` 讓 BE 擋「試算後又被扣點」)。一單只能成功退一次。
 *
 * 冪等鍵(`orderRef` / `refundId`):**10–64 碼英數**(BE 規格)→ 一律用 {@link newPointsWireKey}。
 * 同鍵同參數重送 = 同一筆;重新購買 / 失敗後重新申請要換新鍵。
 *
 * 入口顯不顯:`getStatus` —— `configured=false` / 503 `MENTOR_NOT_CONFIGURED` / 路由不存在的 404
 * = 這個環境沒接(藏入口)。
 */
export interface PointsService {
  /** M1 `GET /wallet/mentor/packages` — 套餐(含稅價 Mentor 算好)。 */
  listPackages(): Promise<PointsPackage[]>;

  /** M2 `GET /wallet/mentor/status` — 本環境有沒有接 Mentor(入口探測)。 */
  getStatus(): Promise<PointsStatus>;

  /**
   * M3 `POST /wallet/mentor/orders` — 建付款單,回訂單(含 `payUrl`)。
   * `orderRef`:FE 鑄的冪等鍵(同一次「前往付款」的重按沿用;換方案換一把)。
   * `returnUrl`:付款完 Mentor 導回來的 Creative 網址,**query 已含 `order_ref`**。
   */
  createOrder(req: { packageCode: string; orderRef: string; returnUrl: string }): Promise<PointsOrder>;

  /** M4 `GET /wallet/mentor/orders` — 本人在目前工作區的訂單(新→舊)+ 點數摘要。 */
  listOrders(q?: { cursor?: string | null; limit?: number }): Promise<PointsOrderPage>;

  /** M5 `GET /wallet/mentor/orders/{ref}` — `refresh: true` = BE 先向 Mentor 收斂(回跳輪詢用)。 */
  getOrder(orderRef: string, opts?: { refresh?: boolean }): Promise<PointsOrder>;

  /** M6 `GET /wallet/mentor/orders/{ref}/pay-url` — 待付款單重取付款頁連結(繼續付款)。 */
  getPayUrl(orderRef: string): Promise<string>;

  /**
   * M7 `GET /wallet/mentor/orders/{ref}/invoice-link` — 電子發票資訊(連結可能沒有)。
   * 404 `MENTOR_REJECTED` + `details.mentor_code`:`ORDER_NOT_FOUND` / `INVOICE_NOT_READY` / `INVOICE_NOT_AVAILABLE`。
   */
  getInvoice(orderRef: string): Promise<PointsInvoice>;

  /** M8 `GET /wallet/mentor/orders/{ref}/refund-quote` — 退款試算。 */
  getRefundQuote(orderRef: string): Promise<PointsRefundQuote>;

  /**
   * M9 `POST /wallet/mentor/orders/{ref}/refunds` — 申請退款,回 Refund。
   * 409:`MENTOR_REFUND_QUOTE_STALE`(重新試算)/ `MENTOR_REFUND_ALREADY_ACTIVE` /
   * `MENTOR_REFUND_ALREADY_SETTLED`(一單一次)/ `MENTOR_REFUND_UNAVAILABLE` / `MENTOR_REFUND_BUSY`。
   */
  requestRefund(orderRef: string, req: { refundId: string; quotedAmount: number }): Promise<PointsRefund>;

  /** M10 `GET /wallet/mentor/refunds/{id}` — `refresh: true` = 先向 Mentor 收斂。 */
  getRefund(refundId: string, opts?: { refresh?: boolean }): Promise<PointsRefund>;
}

/**
 * 冪等鍵(`order_ref` / `refund_id`):BE 規格 10–64 碼英數 → 去掉 `-` 的 uuid(32 碼 hex)。
 * ⚠️ 不要改用 `newIdempotencyKey()`(帶 `-`)—— 那會被 BE 422。
 */
export function newPointsWireKey(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

/** fixture demo 開關的 key(dev 用;值 `1` = 刷卡流程、`atm` = ATM 待付款流程)。 */
export const POINTS_DEMO_KEY = 'hai.pointsDemo';

/**
 * 是否注入 fixture(比照 `isCheckDemoEnabled`):**純 opt-in**,不是任何環境的預設。
 * 用途 = 在瀏覽器裡把購買 / 回跳 / 退款三條流程走一遍(UI 審查)。
 * console 跑 `localStorage.setItem('hai.pointsDemo','1')` + reload;關掉 = `removeItem`。
 */
export function readPointsDemoMode(): 'card' | 'atm' | null {
  try {
    const v = localStorage.getItem(POINTS_DEMO_KEY);
    if (v === '1') return 'card';
    if (v === 'atm') return 'atm';
    return null;
  } catch {
    return null;
  }
}
